import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { ChevronRight, Music, Wine, Users, Truck, Star, ExternalLink } from "lucide-react";

const HERO_BG = "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-5053-2_orig.jpg";
const GALLERY_IMAGES = [
  "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-6658-2_orig.jpg",
  "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-5207-2_orig.jpg",
  "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-6485_orig.jpg",
  "https://www.charlesworth.club/uploads/2/5/8/3/25832620/img-7640_orig.jpg",
];

const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=charlesworth+club";

const REVIEWS = [
  {
    name: "Tom",
    rating: 4.8,
    text: "Brilliant man. Amusing and yet professional the wedding went without a hitch. Book him quickly as he gets booked up quickly! Well done Noah. Such good value for money I have now had him for an apres ski party and my wedding. Will get him again this summer.",
    source: "Hitched"
  },
  {
    name: "Anna",
    rating: 5.0,
    text: "Noah was the best! He listened to everything we asked for in terms of set up and music. He set up the horse box and sorted all the drinks in good time. Wedding drinks and music all in one — couldn't recommend more!",
    source: "Hitched"
  }
];

const HomePage = () => {
  const [activeReview, setActiveReview] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveReview(prev => (prev + 1) % REVIEWS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Mobile Horsebox Bar",
      description: "A beautifully converted 1980s Rice horsebox bringing the party to you"
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: "Live DJ & PA System",
      description: "Professional DJ setup to keep your guests entertained all night"
    },
    {
      icon: <Wine className="w-8 h-8" />,
      title: "Premium Drinks",
      description: "Draught beers, fine wines, spirits, mixers and signature cocktails"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Full Staff Service",
      description: "Professional bartenders and setup included with every package"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto animate-fadeIn">
          <p className="label-text text-yellow-400 mb-6">Mobile Bar & DJ Services</p>
          <h1 className="heading-xl text-white mb-6">
            Charlesworth<br />
            <span className="text-yellow-400">Club</span>
          </h1>
          <p className="text-lg md:text-xl text-neutral-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            Transform your event with our vintage horsebox bar. Perfect for weddings, 
            parties, and festivals. We provide everything you need — all you have to do 
            is send out the invites!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/pricing" className="btn-primary" data-testid="hero-view-packages">
              View Packages
              <ChevronRight className="inline ml-2 w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="hero-contact">
              Get in Touch
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-yellow-400 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-[#050505]" data-testid="features-section">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="label-text text-yellow-400 mb-4">What We Offer</p>
            <h2 className="heading-lg text-white">Everything You Need</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="surface-card p-8 text-center hover:border-yellow-400/30 transition-all duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-yellow-400/30 flex items-center justify-center text-yellow-400">
                  {feature.icon}
                </div>
                <h3 className="heading-md text-white mb-3">{feature.title}</h3>
                <p className="text-body">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding bg-[#0A0A0A]" data-testid="gallery-preview">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="label-text text-yellow-400 mb-4">Our Work</p>
            <h2 className="heading-lg text-white">Gallery</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {GALLERY_IMAGES.map((img, index) => (
              <div 
                key={index} 
                className="aspect-square img-zoom"
                data-testid={`gallery-preview-${index}`}
              >
                <img 
                  src={img} 
                  alt={`Event ${index + 1}`} 
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/gallery" className="btn-secondary" data-testid="view-full-gallery">
              View Full Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="section-padding bg-[#050505]" data-testid="reviews-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="label-text text-yellow-400 mb-4">What People Say</p>
            <h2 className="heading-lg text-white">Reviews</h2>
            <div className="flex items-center justify-center gap-1 mt-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
              ))}
              <span className="text-white font-bold ml-2">4.9</span>
              <span className="text-neutral-500 text-sm ml-1">on Google</span>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="relative min-h-[200px]">
              {REVIEWS.map((review, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-all duration-700 ${
                    index === activeReview
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-4 pointer-events-none'
                  }`}
                  data-testid={`review-${index}`}
                >
                  <div className="surface-card p-8 md:p-10 text-center">
                    <div className="flex items-center justify-center gap-1 mb-6">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={i < Math.round(review.rating) ? "text-yellow-400 fill-yellow-400" : "text-neutral-600"}
                        />
                      ))}
                    </div>
                    <p className="text-neutral-300 text-base md:text-lg leading-relaxed italic mb-6">
                      "{review.text}"
                    </p>
                    <div>
                      <p className="text-white font-semibold">{review.name}</p>
                      <p className="text-neutral-500 text-xs mt-1">via {review.source}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Dots */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {REVIEWS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveReview(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === activeReview ? 'bg-yellow-400 w-6' : 'bg-neutral-600 hover:bg-neutral-500'
                  }`}
                  data-testid={`review-dot-${index}`}
                />
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-neutral-400 hover:text-yellow-400 transition-colors text-sm"
                data-testid="google-reviews-link"
              >
                See all reviews on Google
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-[#050505] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${GALLERY_IMAGES[0]})` }}
          />
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <p className="label-text text-yellow-400 mb-4">Ready to Book?</p>
            <h2 className="heading-lg text-white mb-6">
              Make Your Event Unforgettable
            </h2>
            <p className="text-body mb-10 max-w-xl mx-auto">
              From intimate gatherings to grand celebrations, we have the perfect 
              package for your event. Secure your date with just a £450 deposit.
            </p>
            <Link to="/pricing" className="btn-primary" data-testid="cta-book-now">
              Book Your Event
              <ChevronRight className="inline ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
